'use client';
import { useState, useRef, useEffect, useCallback } from 'react';
import { MapPin, X } from 'lucide-react';
import api from '@/lib/api';

interface City { id: string; name: string; region?: string; }

interface Props {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  error?: boolean;
  // Строгий режим: значение формы меняется ТОЛЬКО при выборе из списка/очистке,
  // произвольный ввод с клавиатуры не сохраняется. Для форм записи города.
  strict?: boolean;
}

export default function CityAutocomplete({
  value,
  onChange,
  placeholder = 'Начните вводить город...',
  className = '',
  error = false,
  strict = false,
}: Props) {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState<City[]>([]);
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [apiFailed, setApiFailed] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => { setQuery(value || ''); }, [value]);

  // Строгий режим: сброс несохранённого ввода к последнему валидному значению.
  const revertToValue = useCallback(() => {
    setQuery(value || '');
    setOpen(false);
  }, [value]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
        setFocused(false);
        // В строгом режиме несохранённый ввод откатываем к валидному значению.
        if (strict && query !== (value || '')) setQuery(value || '');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [strict, query, value]);

  const fetchCities = useCallback(async (q: string) => {
    if (!q || q.length < 2) { setSuggestions([]); setOpen(false); return; }
    try {
      const res = await api.get(`/cities/search?q=${encodeURIComponent(q)}`);
      const cities = res.data.data || [];
      setApiFailed(false);
      setSuggestions(cities);
      setOpen(cities.length > 0);
    } catch {
      setApiFailed(true);
      setSuggestions([]);
      setOpen(false);
    }
  }, []);

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    // В строгом режиме ввод с клавиатуры НЕ меняет значение формы —
    // оно обновится только через handleSelect/handleClear.
    if (!strict) onChange(val);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => fetchCities(val), 200);
  };

  const handleSelect = (city: City) => {
    setQuery(city.name);
    onChange(city.name);
    setSuggestions([]);
    setOpen(false);
  };

  const handleClear = () => {
    setQuery('');
    onChange('');
    setSuggestions([]);
    setOpen(false);
  };

  const handleBlur = () => {
    // Клик по подсказке использует onMouseDown (срабатывает до blur) — выбор успеет пройти.
    if (strict && query !== (value || '')) revertToValue();
  };

  const borderClass = error
    ? 'border-red-400'
    : focused
      ? 'border-brand ring-2 ring-brand/20'
      : 'border-border-input';

  return (
    <div ref={wrapperRef} className="relative">
      <div className={`relative flex items-center border rounded-xl transition-all bg-bg-surface ${borderClass} ${className}`}>
        <MapPin size={16} className="absolute left-3.5 text-gray-400 pointer-events-none flex-shrink-0" />
        <input
          type="text"
          value={query}
          onChange={handleInput}
          onFocus={() => { setFocused(true); if (suggestions.length > 0) setOpen(true); }}
          onBlur={handleBlur}
          placeholder={placeholder}
          autoComplete="off"
          className="w-full bg-transparent pl-9 pr-8 py-3 text-sm text-text-body placeholder-gray-500 outline-none rounded-xl"
        />
        {query && (
          <button type="button" onClick={handleClear} className="absolute right-3 text-gray-400 hover:text-text-secondary transition-colors">
            <X size={15} />
          </button>
        )}
      </div>

      {strict && apiFailed && focused && (
        <p className="mt-1 text-xs text-red-500">Не удалось загрузить список городов. Попробуйте позже — сохранить произвольный город нельзя.</p>
      )}

      {open && suggestions.length > 0 && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-bg-surface border border-border rounded-xl shadow-lg overflow-hidden">
          {suggestions.map((city) => (
            <button
              key={city.id}
              type="button"
              onMouseDown={() => handleSelect(city)}
              className="w-full text-left px-4 py-2.5 text-sm hover:bg-brand/5 transition-colors flex items-start gap-2.5"
            >
              <MapPin size={13} className="text-gray-400 flex-shrink-0 mt-0.5" />
              <div>
                <span className="text-text-body font-medium">{city.name}</span>
                {city.region && <span className="text-gray-400 text-xs ml-2">{city.region}</span>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
